import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuthenticatedRequestor } from '@app/rest/rest.hooks'

import {
  getDomainEvents,
  getDomainEventSources,
  getDomainEventSubjects,
  getDomainEventTypes,
} from './domain-events.rest'

import type {
  IDomainEvent,
  IUseDomainEvents,
  IUseDomainEventFilters,
} from './domain-events.interface'

export const useInterval = (callback, delay) => {
  const savedCallback = useRef<() => void>()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (delay !== null) {
      const id = setInterval(tick, delay)
      return () => clearInterval(id)
    } else {
      return () => {
        return
      }
    }
  }, [delay])
}

export function useDomainEvents(): IUseDomainEvents {
  const [domainEvents, setDomainEvents] = useState<IDomainEvent[]>([])
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalNumberOfRecords, setTotalNumberOfRecords] = useState<number>(0)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const requestor = useAuthenticatedRequestor()

  const fetchData = useCallback(
    async (
      pageNumber,
      recordsPerPage,
      pausedTime,
      sources: string[],
      subjects: string[],
      types: string[],
    ) => {
      setIsLoading(true)
      const response = await requestor(
        getDomainEvents(
          pageNumber,
          recordsPerPage,
          pausedTime,
          sources,
          subjects,
          types,
        ),
      )

      if (response.status === 200) {
        setDomainEvents(response.data.domainEvents)
        setPageCount(response.data.pageCount)
        setTotalNumberOfRecords(response.data.totalNumberOfRecords)
      } else {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }

      setIsLoading(false)
    },
    [requestor],
  )

  useEffect(() => {
    fetchData(1, 50, null, [], [], [])
  }, [fetchData])

  return {
    domainEvents,
    isLoading,
    pageCount: pageCount,
    totalNumberOfRecords: totalNumberOfRecords,
    refresh: fetchData,
  }
}

export function useDomainEventFilters(): IUseDomainEventFilters {
  const [sources, setSources] = useState<string[]>([])
  const [subjects, setSubjects] = useState<string[]>([])
  const [types, setTypes] = useState<string[]>([])
  const requestor = useAuthenticatedRequestor()

  useEffect(() => {
    const updateDomainEventSources = async () => {
      const response = await requestor(getDomainEventSources())

      if (response.status === 200) {
        setSources(response.data.sources)
      }
    }

    const updateDomainEventSubjects = async () => {
      const response = await requestor(getDomainEventSubjects())

      if (response.status === 200) {
        setSubjects(response.data.subjects)
      }
    }

    const updateDomainEventTypes = async () => {
      const response = await requestor(getDomainEventTypes())

      if (response.status === 200) {
        setTypes(response.data.types)
      }
    }

    updateDomainEventSources()
    updateDomainEventSubjects()
    updateDomainEventTypes()
  }, [requestor])

  return {
    sources,
    subjects,
    types,
  }
}
